import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Check } from "lucide-react";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link. Please request a new one.");
      navigate("/forgot-password");
    }
  }, [token, navigate]);

  const rules = [
    { label: "At least 8 characters", ok: password.length >= 8 },
    { label: "Passwords match", ok: password === confirm && confirm.length > 0 },
  ];

  const valid = rules.every((r) => r.ok);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset successfully!");
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to reset password. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "var(--secondary)" }}>
        <div className="bg-white rounded-2xl border p-10 w-full max-w-md text-center" style={{ borderColor: "var(--border)" }}>
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: "#dcfce7" }}
          >
            <Check size={28} className="text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: "Fraunces, serif" }}>
            Password reset!
          </h1>
          <p className="text-sm opacity-70 leading-relaxed">
            Your password has been changed successfully. Redirecting you to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "var(--secondary)" }}>
      <div className="bg-white rounded-2xl border p-10 w-full max-w-md" style={{ borderColor: "var(--border)" }}>
        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Fraunces, serif" }}>
          Choose a new password
        </h1>
        <p className="text-sm opacity-60 mb-8 leading-relaxed">
          Enter a new password for your MoolTax account. Make sure it's at least
          8 characters long.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="password">New password</Label>
            <div className="relative mt-1">
              <Input
                id="password"
                type={showPw ? "text" : "password"}
                placeholder="Min 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-80"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input
              id="confirm"
              type="password"
              placeholder="Repeat your password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="mt-1"
              data-testid="confirm-password-input"
            />
          </div>

          {/* Password rules */}
          {(password || confirm) && (
            <ul className="space-y-1.5">
              {rules.map((r, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: r.ok ? "#dcfce7" : "#f3f4f6" }}
                  >
                    {r.ok && <Check size={10} className="text-green-600" />}
                  </div>
                  <span style={{ color: r.ok ? "#16a34a" : "var(--foreground)", opacity: r.ok ? 1 : 0.5 }}>
                    {r.label}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-600">{error}</p>
              <button
                type="button"
                className="text-sm underline text-red-500 mt-1"
                onClick={() => navigate("/forgot-password")}
              >
                Request a new reset link
              </button>
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting || !valid}
            className="w-full btn-primary"
            data-testid="reset-submit-btn"
          >
            {submitting ? "Resetting..." : "Reset password"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;